import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { getCoreRowModel, useReactTable } from "@tanstack/react-table"

import { EntityTable } from "@/components/EntityTable"
import { VehicleListRow } from "@/hooks/useVehicles"

import { vehicleColumns } from "./vehicleColumns"

const PAGE_SIZE = 50

interface VehicleDirectoryProps {
  vehicles: VehicleListRow[]
  selectedVehicleId: string | null
  onSelectVehicle: (vehicle: VehicleListRow) => void
}

const VehicleDirectory = ({
  vehicles,
  selectedVehicleId,
  onSelectVehicle,
}: VehicleDirectoryProps) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Filters change the underlying list, so a stale offset into the new list would skip rows. Keyed
  // on length rather than identity: a re-render that hands back an equal-but-new array would
  // otherwise reset paging, and reset-during-render would loop against any state above us.
  useEffect(() => {
    setVisibleCount(PAGE_SIZE)
  }, [vehicles.length])

  const hasMore = visibleCount < vehicles.length
  // useReactTable re-renders itself whenever `data` changes identity, so a fresh slice on every
  // render loops until the tab locks up.
  const visibleVehicles = useMemo(() => vehicles.slice(0, visibleCount), [vehicles, visibleCount])

  const table = useReactTable({
    data: visibleVehicles,
    columns: vehicleColumns,
    getCoreRowModel: getCoreRowModel(),
  })

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting && hasMore) {
        setVisibleCount((count) => count + PAGE_SIZE)
      }
    },
    [hasMore],
  )

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(handleIntersection, {
      root: scrollContainerRef.current,
      threshold: 0.1,
    })
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [handleIntersection])

  return (
    <EntityTable
      table={table}
      getRowProps={(vehicle) => ({
        key: vehicle.vehicle.id,
        isSelected: selectedVehicleId === vehicle.vehicle.id,
        onClick: (e) => {
          e.stopPropagation()
          onSelectVehicle(vehicle)
        },
      })}
      hasMore={hasMore}
      scrollContainerRef={scrollContainerRef}
      sentinelRef={sentinelRef}
      footer={`${vehicles.length} ${vehicles.length === 1 ? "vehicle" : "vehicles"} fetched`}
    />
  )
}

export default VehicleDirectory
